import {range} from 'lodash';
import type {IDoCheck, ILogService, IScope} from 'angular';
import type {Component, OnChanges, OnChangesObject} from 'typings/portaro.fe.types';
import type {PdfError, PdfInfo} from '../pdf-viewer';
import type {PDFDocumentProxy, PDFPageProxy} from 'pdfjs-dist/types/src/display/api';

async function getDocument(fileUrl: string): Promise<PDFDocumentProxy> {

    const pdfjs = await import(/* webpackChunkName: "pdfjs-dist" */ 'pdfjs-dist/build/pdf');
    const {default: PdfjsWorker} = await import(/* webpackChunkName: "pdfjs-dist" */ 'pdfjs-dist/build/pdf.worker');

    pdfjs.GlobalWorkerOptions.workerPort = new PdfjsWorker();

    return pdfjs.getDocument(fileUrl).promise;
}

function getPages(pdf: PDFDocumentProxy, first: number, last: number): Promise<PDFPageProxy[]> {
    const allPages = range(first, last + 1).map((pageNumber) => pdf.getPage(pageNumber));
    return Promise.all(allPages as unknown as Array<Promise<PDFPageProxy>>);
}

export abstract class KpPdfDataComponentController implements KpPdfDataComponentBindings, OnChanges<KpPdfDataComponentBindings>, IDoCheck {

    private readonly LARGE_FILE_THRESHOLD = 4 * 1024 * 1024;

    public fileUrl: string;
    public fileSize: number | null;
    private currentPage: number;

    public abstract onDocumentErrored(locals: { $error: any; });

    public abstract onDocumentRendered(locals: { $info: any });

    public abstract onPageCount(locals: { $pageCount: number });

    public abstract onPageFocus(locals: { $pageNumber: number });

    public loaded: boolean;
    private pdf: PDFDocumentProxy;
    private pdfOld: PDFDocumentProxy;
    private pages: PDFPageProxy[] = [];
    private cursor = 0;

    /*@ngInject*/
    constructor(private $log: ILogService, private $scope: IScope, private PDF_BUFFER_LENGTH: number) {
    }

    public $onChanges(onChangesObj: OnChangesObject<KpPdfDataComponentBindings>): void {
        if (onChangesObj.fileUrl && onChangesObj.fileUrl.currentValue) {
            this.loaded = false;
            getDocument(onChangesObj.fileUrl.currentValue)
                .then((pdf) => this.$scope.$apply(() => {
                    this.pdf = pdf;
                    this.loaded = true;
                }))
                .catch((response) => {
                    this.onDocumentErrored({$error: {text: 'Failed to retrieve PDF', response}});
                    this.$log.warn('Failed to retrieve PDF', response);
                });
        }
    }

    public $doCheck(): void {
        if (this.pdf !== this.pdfOld) {
            if (!this.pdf) {
                return;
            }
            if (this.pdfOld) {
                this.getDefaults();
            }

            this.onPageCount({$pageCount: this.pageCount});
            this.fetchPages();

            this.pdfOld = this.pdf;
        }
    }

    public onPageRendered({text, page}: PdfInfo) {
        this.$log.info(text, page);
    }

    public onPageErrored({text, error, page}: PdfError) {
        this.$log.info('Error!', text, error, page);
    }

    public fetchPages(currentPage?: number) {
        if (!this.pdf) {
            this.currentPage = currentPage || this.currentPage || 0;
            return;
        }

        if (this.currentPage) {
            currentPage = this.currentPage;
            this.currentPage = null;
        }

        if (this.pageCount > 0 && this.pages.length === this.pageCount) {
            return;
        }

        const startIndex = this.pages.length;
        if (this.cursor > startIndex) {
            return;
        }

        const startPage = startIndex + 1;
        const endPage = Math.min(Math.max(currentPage || 0, startIndex + this.PDF_BUFFER_LENGTH), this.pageCount);
        this.cursor = endPage;

        this.$log.info(`Fetching pages ${startPage} to ${endPage}`);
        return getPages(this.pdf, startPage, endPage)
            .then((pages) => {
                const deleteCount = 0;
                this.pages.splice(startIndex, deleteCount, ...pages);

                this.$scope.$applyAsync();

                return this.pages;
            })
            .catch((response) => {
                this.onDocumentErrored({$error: {text: 'Failed to retrieve pages', response}});
                this.$log.info('Failed to retrieve pages', response);
            });
    }

    private getDefaults() {
        this.cursor = 0;
        this.pages = [];
    }

    get pageCount(): number {
        return this.pdf ? this.pdf.numPages : 0;
    }

    get showLargeFileWarn(): boolean {
        return this.fileSize !== null && this.fileSize > this.LARGE_FILE_THRESHOLD;
    }
}

export default class KpPdfDataComponent implements Component<KpPdfDataComponentBindings> {
    public static componentName = 'kpPdfData';

    public template = `
        <ng-transclude ng-transclude-slot="previewSlot"></ng-transclude>
        <ng-transclude ng-transclude-slot="documentSlot"></ng-transclude>
        <div ng-hide="pdfDataController.loaded" style="text-align: center;">
            <jp-loading></jp-loading>
            <span ng-show="pdfDataController.showLargeFileWarn"><strong>Vyčkejte prosím, soubor je velký ({{pdfDataController.fileSize | bytes}}) a načítání může trvat dlouho.</strong></span>
        </div>
    `;

    public transclude = {
        previewSlot: '?previewSlot',
        documentSlot: '?documentSlot'
    };

    public bindings = {
        fileUrl: '<',
        fileSize: '<',
        onDocumentErrored: '&?',
        onDocumentRendered: '&?',
        onPageCount: '&?',
        onPageFocus: '&?'
    } as const;

    public controller = KpPdfDataComponentController;
    public controllerAs = 'pdfDataController';
}

interface KpPdfDataComponentBindings {
    fileUrl: string;
    fileSize: number | null;

    onDocumentErrored(locals: { $error: any });

    onDocumentRendered(locals: { $info: any });

    onPageCount(locals: { $pageCount: number });

    onPageFocus(locals: { $pageNumber: number });
}
